import { Alert, Button } from '@edgebox/react-components';
import React, { useCallback } from 'react';
import type { ClientContentRecommendationEntity } from '@edgebox/sync-core-rest-client';
import { DEFAULT_MARKDOWN_CONTENT_CLASS_NAME, MarkdownContentBox } from './MarkdownContentBox';
import { ExtractedQaPair, toQaPairSourceLabel } from './qaPairs.client';
import {
  SUGGESTED_FAQ_SKELETON_COUNT,
  SuggestedFaqDraftState,
  SuggestedFaqRow,
  SuggestedFaqRowState,
  SuggestedFaqSkeleton,
  SuggestedFaqTermResolver,
} from './SuggestedFaqs';

/**
 * The FAQ tab's panel: the questions a page already answers, and the FAQs
 * suggested for it.
 */

const FAQ_PANEL_HEADING_ID = 'faq-panel-heading';
const FAQ_QA_PAIRS_HEADING_ID = 'faq-qa-pairs-heading';
const FAQ_SUGGESTED_HEADING_ID = 'faq-suggested-heading';
const FAQ_QA_PAIR_SKELETON_COUNT = 3;

function FaqErrorAlert(props: { message: string; onRetry: () => void }) {
  return (
    <Alert variant="danger" className="flex-shrink-0">
      {props.message}{' '}
      <Button variant="danger" size="sm" onClick={props.onRetry}>
        Try again
      </Button>
    </Alert>
  );
}

function FaqQaPairSkeleton() {
  return (
    <li data-testid="faq-qa-pair-skeleton" aria-hidden="true" className="rounded-xl p-4 ring-1 ring-inset ring-slate-200/80">
      <div className="h-4 w-2/3 rounded bg-slate-200" />
      <div className="mt-3 h-3 w-full rounded bg-slate-100" />
      <div className="mt-2 h-3 w-5/6 rounded bg-slate-100" />
    </li>
  );
}

function FaqQaPairItem(props: { pair: ExtractedQaPair }) {
  const { pair } = props;
  const sourceLabel = toQaPairSourceLabel(pair.source);
  const showSourceHeading = pair.source === 'model' && !!pair.sourceHeading;

  return (
    <li className="rounded-xl p-4 ring-1 ring-inset ring-slate-200/80">
      <h4 className="text-sm font-semibold text-slate-900">{pair.question}</h4>
      <div className="mt-2">
        <MarkdownContentBox
          markdown={pair.answer}
          emptyText="No answer available."
          containerClassName="m-0"
          contentClassName={DEFAULT_MARKDOWN_CONTENT_CLASS_NAME}
        />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 font-semibold">
          {sourceLabel}
        </span>
        {showSourceHeading ? <span className="text-slate-400">{pair.sourceHeading}</span> : null}
      </div>
    </li>
  );
}

type SuggestedFaqsSectionState = 'empty' | 'error' | 'loading' | 'list';

export interface ISuggestedFaqsSectionProps {
  state: SuggestedFaqsSectionState;
  onRetry?: () => void;
  suggestions?: ClientContentRecommendationEntity[];
  rowStates?: Record<string, SuggestedFaqRowState>;
  pageKey?: string;
  resolveTerm?: SuggestedFaqTermResolver;
  onOpenOptimizationTab?: () => void;
  canOpenOptimizationTab?: boolean;
  /**
   * Where the draft of each suggestion this visit added stands. A row added in
   * an earlier visit is absent from the map and says nothing about a draft.
   */
  draftStates?: Record<string, SuggestedFaqDraftState>;
  /** True when the page carries more suggestions than this screen reads in one go. */
  capped?: boolean;
  /** True when this page's optimizations could not all be read. */
  addedStateIncomplete?: boolean;
  onAdd?: (suggestion: ClientContentRecommendationEntity) => Promise<void>;
  onDismiss?: (suggestion: ClientContentRecommendationEntity) => Promise<void>;
  announcement?: string;
  /**
   * Counts the announcements, so the same word twice still changes the region's
   * text and is announced twice. The region itself is never replaced.
   */
  announcementKey?: number;
  onAnnounce?: (message: string) => void;
}

/** The Suggested FAQs section: the page's open FAQ suggestions, most urgent first. */
export function SuggestedFaqsSection(props: ISuggestedFaqsSectionProps) {
  const { onRetry, suggestions, rowStates, pageKey, resolveTerm, onOpenOptimizationTab, onAdd, onDismiss, announcement, onAnnounce } =
    props;
  const announcementKey = props.announcementKey ?? 0;

  const noop = useCallback(async () => undefined, []);
  const announce = onAnnounce ?? (() => undefined);

  return (
    <section aria-labelledby={FAQ_SUGGESTED_HEADING_ID} className="mt-8">
      <h3 id={FAQ_SUGGESTED_HEADING_ID} className="text-sm font-bold uppercase tracking-wider text-slate-500">
        Suggested FAQs
      </h3>
      <p aria-live="polite" className="sr-only">
        {announcement ? `${announcement}${'\u200B'.repeat(announcementKey % 2)}` : ''}
      </p>
      <div className="mt-3">
        {props.state === 'error' ? (
          <FaqErrorAlert message="Couldn't load suggested FAQs." onRetry={onRetry ?? (() => undefined)} />
        ) : props.state === 'loading' ? (
          <ul className="space-y-3">
            {Array.from({ length: SUGGESTED_FAQ_SKELETON_COUNT }, (_unused, index) => (
              <SuggestedFaqSkeleton key={index} />
            ))}
          </ul>
        ) : props.state === 'list' && suggestions?.length ? (
          <ol className="space-y-3">
            {suggestions.map((suggestion) => (
              <SuggestedFaqRow
                key={suggestion.id}
                suggestion={suggestion}
                pageKey={pageKey ?? ''}
                resolveTerm={resolveTerm ?? (() => undefined)}
                state={rowStates?.[suggestion.id] ?? 'open'}
                onOpenOptimizationTab={onOpenOptimizationTab ?? (() => undefined)}
                canOpenOptimizationTab={props.canOpenOptimizationTab === true}
                draftState={props.draftStates?.[suggestion.id]}
                onAdd={onAdd ?? noop}
                onDismiss={onDismiss ?? noop}
                announce={announce}
              />
            ))}
            {props.capped ? (
              <li className="text-sm text-slate-500">
                This page carries more suggested FAQs than this list reads in one go; the most urgent ones are shown.
              </li>
            ) : null}
            {props.addedStateIncomplete ? (
              <li className="text-sm text-slate-500">
                This page&rsquo;s optimizations couldn&rsquo;t all be read, so a FAQ already added may still show the Add to page action.
              </li>
            ) : null}
          </ol>
        ) : (
          <p className="text-sm text-slate-500">No FAQs suggested for this page yet.</p>
        )}
      </div>
    </section>
  );
}

export function FaqPanel(props: {
  pairs: ExtractedQaPair[];
  expectedCount?: number;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  suggested?: ISuggestedFaqsSectionProps;
}) {
  const { pairs, expectedCount, loading, error, onRetry, suggested } = props;

  const qaPairCount = pairs.length;
  const suggestedCount = suggested?.state === 'list' ? (suggested.suggestions?.length ?? 0) : 0;
  const isPartial = !loading && !error && qaPairCount > 0 && typeof expectedCount === 'number' && qaPairCount < expectedCount;

  const countParts: string[] = [];
  if (qaPairCount > 0) {
    countParts.push(`${qaPairCount} Q&A ${qaPairCount === 1 ? 'pair' : 'pairs'}`);
  }
  if (suggestedCount > 0) {
    countParts.push(`${suggestedCount} suggested`);
  }
  const countLine = countParts.join(' · ');

  return (
    <div className="bg-white border border-slate-200 border-t-0 rounded-b-lg p-6" role="tabpanel" aria-labelledby={FAQ_PANEL_HEADING_ID}>
      <h2 id={FAQ_PANEL_HEADING_ID} className="text-lg font-semibold text-slate-900">
        FAQ
      </h2>
      <p className="mt-1 text-sm text-slate-500">The questions this page answers, and FAQs suggested for it</p>
      {loading ? (
        <p role="status" className="mt-2 text-sm text-slate-500">
          Loading FAQ…
        </p>
      ) : countLine ? (
        <p className="mt-2 text-sm font-semibold text-slate-700">{countLine}</p>
      ) : null}

      <section aria-labelledby={FAQ_QA_PAIRS_HEADING_ID} className="mt-6">
        <h3 id={FAQ_QA_PAIRS_HEADING_ID} className="text-sm font-bold uppercase tracking-wider text-slate-500">
          Q&amp;A pairs
          {!loading && !error && qaPairCount > 0 ? <span className="ml-2 text-slate-400">{qaPairCount}</span> : null}
        </h3>
        <div className="mt-3">
          {loading ? (
            <ul className="space-y-3">
              {Array.from({ length: FAQ_QA_PAIR_SKELETON_COUNT }, (_unused, index) => (
                <FaqQaPairSkeleton key={index} />
              ))}
            </ul>
          ) : error ? (
            <FaqErrorAlert message="Couldn't load this page's Q&A pairs." onRetry={onRetry} />
          ) : qaPairCount === 0 ? (
            <p className="text-sm text-slate-500">No Q&amp;A pairs found on this page.</p>
          ) : (
            <>
              <ol className="space-y-3">
                {pairs.map((pair) => (
                  <FaqQaPairItem key={`${pair.position}:${pair.question}`} pair={pair} />
                ))}
              </ol>
              {isPartial ? <p className="mt-3 text-sm text-amber-700">Some answers couldn&apos;t be analysed.</p> : null}
            </>
          )}
        </div>
      </section>

      <SuggestedFaqsSection {...(suggested ?? { state: 'empty' })} />
    </div>
  );
}
